import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const fmtDate = (dt) =>
    dt ? new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StarDisplay = ({ count }) => (
    <span style={{ color: '#f59e0b', fontSize: 14 }}>
        {'★'.repeat(count)}{'☆'.repeat(5 - count)}
    </span>
);

const MAX_VISIBLE = 10;

const FeedbackTab = ({ showToast }) => {
    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        fetchFeedback();
    }, []);

    const fetchFeedback = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('feedback')
            .select('*')
            .order('created_at', { ascending: false });
        if (error) {
            showToast && showToast('Error loading feedback', 'error');
        } else {
            setFeedback(data || []);
        }
        setLoading(false);
    };

    const toggleShowOnHomepage = async (id, currentStatus) => {
        // Block enabling if limit already reached
        if (!currentStatus && shown >= MAX_VISIBLE) {
            showToast && showToast(`Limit reached (${MAX_VISIBLE}/${MAX_VISIBLE}). Hide another review first.`, 'error');
            return;
        }
        const { error } = await supabase
            .from('feedback')
            .update({ show_on_homepage: !currentStatus })
            .eq('id', id);
        if (error) {
            showToast && showToast('Failed to update', 'error');
        } else {
            showToast && showToast(
                !currentStatus ? 'Now showing on homepage' : 'Removed from homepage',
                'success'
            );
            fetchFeedback();
        }
    };

    const deleteFeedback = async (id) => {
        if (!window.confirm('Delete this feedback?')) return;
        const { error } = await supabase.from('feedback').delete().eq('id', id);
        if (error) {
            showToast && showToast('Delete failed', 'error');
        } else {
            showToast && showToast('Feedback deleted', 'success');
            fetchFeedback();
        }
    };

    const feedbackLink = `${window.location.origin}/feedback`;

    const handleCopy = () => {
        navigator.clipboard.writeText(feedbackLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const shown = feedback.filter(f => f.show_on_homepage).length;

    return (
        <div style={{ padding: '4px 0' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-1)', margin: 0 }}>
                        <i className="fa-solid fa-star" style={{ color: '#f59e0b', marginRight: 10 }} />
                        Feedback Manager
                    </h2>
                    <p style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>
                        {feedback.length} total &nbsp;·&nbsp;
                        <span style={{
                            fontWeight: 600,
                            color: shown >= MAX_VISIBLE ? 'var(--red)' : shown > 0 ? 'var(--green)' : 'var(--text-3)'
                        }}>
                            {shown}/{MAX_VISIBLE}
                        </span>
                        &nbsp;shown on homepage
                        {shown >= MAX_VISIBLE && (
                            <span style={{ marginLeft: 8, fontSize: 11, background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid #fecaca', borderRadius: 20, padding: '1px 8px', fontWeight: 600 }}>
                                LIMIT REACHED
                            </span>
                        )}
                    </p>
                </div>
            </div>

            {/* Shareable Link Card */}
            <div style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #fefce8 100%)',
                border: '1px solid #bfdbfe',
                borderRadius: 14,
                padding: '20px 24px',
                marginBottom: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
            }}>
                <div style={{ flex: '0 0 auto', width: 44, height: 44, borderRadius: 12, background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fa-solid fa-link" style={{ color: '#fff', fontSize: 18 }} />
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
                        Shareable Feedback Link
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                            type="text"
                            value={feedbackLink}
                            readOnly
                            style={{
                                flex: 1,
                                minWidth: 200,
                                padding: '8px 12px',
                                border: '1px solid #dbeafe',
                                borderRadius: 8,
                                fontSize: 13,
                                background: '#fff',
                                color: 'var(--text-1)',
                                fontFamily: 'monospace',
                            }}
                        />
                        <button
                            onClick={handleCopy}
                            style={{
                                padding: '8px 18px',
                                background: copied ? 'var(--green)' : 'var(--blue)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 8,
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                transition: 'background 0.2s',
                            }}
                        >
                            <i className={copied ? 'fa-solid fa-check' : 'fa-solid fa-copy'} />
                            {copied ? 'Copied!' : 'Copy Link'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Feedback Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>
                    <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 28, marginBottom: 12, display: 'block' }} />
                    Loading feedback...
                </div>
            ) : feedback.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)', background: 'var(--bg-card)', borderRadius: 14, border: '1px dashed var(--border)' }}>
                    <i className="fa-regular fa-face-smile" style={{ fontSize: 36, marginBottom: 12, display: 'block' }} />
                    <p style={{ fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>No submitted feedback yet</p>
                    <p style={{ fontSize: 13, marginTop: 4 }}>Share the link above to collect customer feedback.</p>
                </div>
            ) : (
                    <div style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-card2)', borderBottom: '1px solid var(--border)' }}>
                                    {['Customer', 'Feedback', 'Rating', 'Place Visited', 'Date', 'Homepage', 'Action'].map(h => (
                                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {feedback.map((item, idx) => (
                                    <tr key={item.id} style={{ borderBottom: idx < feedback.length - 1 ? '1px solid var(--border)' : 'none', transition: 'background 0.15s' }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                                        onMouseLeave={e => e.currentTarget.style.background = ''}
                                    >
                                        <td style={{ padding: '14px 16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <div style={{ width: 34, height: 34, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#2563eb', flexShrink: 0 }}>
                                                    {item.name?.charAt(0).toUpperCase()}
                                                </div>
                                                <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-1)' }}>{item.name}</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '14px 16px', maxWidth: 280 }}>
                                            <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                "{item.message}"
                                            </p>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <StarDisplay count={item.rating} />
                                            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{item.rating}/5</div>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--text-2)', background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 9px' }}>
                                                <i className="fa-solid fa-location-dot" style={{ color: '#dc2626', fontSize: 11 }} />
                                                {item.visited}
                                            </span>
                                        </td>
                                        <td style={{ padding: '14px 16px', fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                                            {fmtDate(item.created_at)}
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <button
                                                onClick={() => toggleShowOnHomepage(item.id, item.show_on_homepage)}
                                                style={{
                                                    padding: '5px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                                    background: item.show_on_homepage ? 'var(--green-dim)' : 'var(--bg-card2)',
                                                    color: item.show_on_homepage ? 'var(--green)' : 'var(--text-3)',
                                                    border: `1px solid ${item.show_on_homepage ? '#6ee7b7' : 'var(--border)'}`,
                                                    display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s',
                                                }}
                                            >
                                                <i className={item.show_on_homepage ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'} />
                                                {item.show_on_homepage ? 'Visible' : 'Hidden'}
                                            </button>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <button onClick={() => deleteFeedback(item.id)} title="Delete" style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid #fecaca', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>
                                                <i className="fa-solid fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
            )}
        </div>
    );
};

export default FeedbackTab;
